"use client";

import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils/cn";
import GhostMobileDisclosure from "./GhostMobileDisclosure";

type GhostResponsiveDisclosureBreakpoint = "sm" | "md" | "lg" | "xl" | "2xl";

interface GhostResponsiveDisclosureProps {
  collapsedLabel: string;
  expandedLabel?: string;
  children: ReactNode;
  expandFrom?: GhostResponsiveDisclosureBreakpoint;
  className?: string;
  summaryClassName?: string;
  contentClassName?: string;
  expandedWrapperClassName?: string;
}

const breakpointQueries: Record<GhostResponsiveDisclosureBreakpoint, string> = {
  sm: "(min-width: 640px)",
  md: "(min-width: 768px)",
  lg: "(min-width: 1024px)",
  xl: "(min-width: 1280px)",
  "2xl": "(min-width: 1536px)",
};

export default function GhostResponsiveDisclosure({
  collapsedLabel,
  expandedLabel,
  children,
  expandFrom = "md",
  className,
  summaryClassName,
  contentClassName,
  expandedWrapperClassName,
}: GhostResponsiveDisclosureProps) {
  const [isExpandedLayout, setIsExpandedLayout] = useState(false);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(breakpointQueries[expandFrom]);
    const updateLayout = () => {
      setIsExpandedLayout(mediaQueryList.matches);
    };

    updateLayout();
    mediaQueryList.addEventListener("change", updateLayout);

    return () => {
      mediaQueryList.removeEventListener("change", updateLayout);
    };
  }, [expandFrom]);

  if (isExpandedLayout) {
    return <div className={cn(expandedWrapperClassName)}>{children}</div>;
  }

  return (
    <GhostMobileDisclosure
      collapsedLabel={collapsedLabel}
      expandedLabel={expandedLabel}
      className={className}
      summaryClassName={summaryClassName}
      contentClassName={contentClassName}
    >
      {children}
    </GhostMobileDisclosure>
  );
}
